// tippi/src/pages/admin/settings/AdminSettings.js
import React, { useState, useEffect, useRef } from 'react';
import { useAdminSettings } from '../../../hooks/useAdminSettings';
import { COLORS, SHADOWS } from '../../../styles/tokens';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { adminApi } from '../../../firebase/functionsService';

// ── Shared UI helpers ──────────────────────────────────────────────────────

function SectionCard({ icon, title, children, onSave, saveStatus }) {
  return (
    <div style={{
      background: COLORS.white, borderRadius: 12, boxShadow: SHADOWS.sm,
      padding: 24, marginBottom: 20,
    }}>
      <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: COLORS.textDark, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>{icon}</span>{title}
      </h2>
      <hr style={{ border: 'none', borderTop: `1px solid ${COLORS.border}`, marginBottom: 20 }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {children}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12, marginTop: 20 }}>
        {saveStatus === 'ok'  && <span style={{ fontSize: 13, color: COLORS.success }}>✓ Lagret</span>}
        {saveStatus === 'err' && <span style={{ fontSize: 13, color: COLORS.danger }}>Kunne ikke lagre</span>}
        <button
          onClick={onSave}
          style={{
            padding: '9px 20px', borderRadius: 8, border: 'none',
            background: COLORS.primary, color: COLORS.onPrimary,
            fontWeight: 600, fontSize: 14, cursor: 'pointer',
          }}
        >
          Lagre
        </button>
      </div>
    </div>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: COLORS.textDark, marginBottom: 6 }}>
        {label}
      </label>
      {children}
      {error && <p style={{ margin: '4px 0 0', fontSize: 12, color: COLORS.danger }}>{error}</p>}
    </div>
  );
}

function NumInput({ value, onChange, min, max, disabled }) {
  return (
    <input
      type="number" value={value} min={min} max={max} disabled={disabled}
      onChange={e => onChange(Number(e.target.value))}
      style={{
        width: '100%', padding: '9px 10px', borderRadius: 7,
        border: `1.5px solid ${COLORS.border}`, fontSize: 14, boxSizing: 'border-box',
        background: COLORS.white, color: COLORS.textDark,
      }}
    />
  );
}

function Toggle({ checked, onChange, disabled, ariaLabel }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: disabled ? 'default' : 'pointer' }}>
      <div style={{ position: 'relative', width: 44, height: 24 }}>
        <input
          type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
          disabled={disabled} aria-label={ariaLabel}
          style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }}
        />
        <div style={{
          position: 'absolute', inset: 0, borderRadius: 12,
          background: checked ? COLORS.primary : COLORS.border,
          transition: 'background 0.2s',
        }} />
        <div style={{
          position: 'absolute', top: 3, left: checked ? 23 : 3,
          width: 18, height: 18, borderRadius: '50%', background: '#fff',
          transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }} />
      </div>
    </label>
  );
}

// ── useSaveState helper ────────────────────────────────────────────────────

function useSaveState() {
  const [status, setStatus] = useState(null); // null | 'ok' | 'err'
  const timerRef = useRef(null);

  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  async function run(fn) {
    try {
      await fn();
      setStatus('ok');
    } catch {
      setStatus('err');
    } finally {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setStatus(null), 3000);
    }
  }
  return [status, run];
}

// ── Placeholder section components (filled in Tasks 5-9) ──────────────────

function validatePricing(f) {
  const errs = {};
  if (!Number.isInteger(f.baseRatePerKm) || f.baseRatePerKm <= 0)
    errs.baseRatePerKm = 'Må være et heltall større enn 0';
  if (!Number.isInteger(f.platformCommissionPct) || f.platformCommissionPct < 0 || f.platformCommissionPct > 100)
    errs.platformCommissionPct = 'Må være et heltall mellom 0 og 100';
  if (!Number.isInteger(f.minOrderPriceKr) || f.minOrderPriceKr < 0)
    errs.minOrderPriceKr = 'Må være ≥ 0';
  if (!Number.isInteger(f.maxOrderPriceKr) || f.maxOrderPriceKr <= f.minOrderPriceKr)
    errs.maxOrderPriceKr = 'Må være større enn minimumspris';
  return errs;
}

function PricingSection({ settings, savePricing, disabled }) {
  const [f, setF] = useState({
    baseRatePerKm:         settings.baseRatePerKm,
    platformCommissionPct: settings.platformCommissionPct,
    minOrderPriceKr:       settings.minOrderPriceKr,
    maxOrderPriceKr:       settings.maxOrderPriceKr,
    invoiceIntervalDays:   settings.invoiceIntervalDays,
  });
  const [errs, setErrs]       = useState({});
  const [status, runSave]     = useSaveState();

  // Re-initialise when settings load
  useEffect(() => { setF({
    baseRatePerKm:         settings.baseRatePerKm,
    platformCommissionPct: settings.platformCommissionPct,
    minOrderPriceKr:       settings.minOrderPriceKr,
    maxOrderPriceKr:       settings.maxOrderPriceKr,
    invoiceIntervalDays:   settings.invoiceIntervalDays,
  }); }, [settings]);

  function set(key) { return val => setF(prev => ({ ...prev, [key]: val })); }

  function handleSave() {
    const e = validatePricing(f);
    setErrs(e);
    if (Object.keys(e).length > 0) return;
    runSave(() => savePricing(f));
  }

  return (
    <SectionCard icon="💰" title="Priser & økonomi" onSave={handleSave} saveStatus={status}>
      <Field label="Basispris per km (kr)" error={errs.baseRatePerKm}>
        <NumInput value={f.baseRatePerKm} onChange={set('baseRatePerKm')} min={1} disabled={disabled} />
      </Field>
      <Field label="Plattformprovisjon (%)" error={errs.platformCommissionPct}>
        <NumInput value={f.platformCommissionPct} onChange={set('platformCommissionPct')} min={0} max={100} disabled={disabled} />
      </Field>
      <Field label="Minimumspris oppdrag (kr)" error={errs.minOrderPriceKr}>
        <NumInput value={f.minOrderPriceKr} onChange={set('minOrderPriceKr')} min={0} disabled={disabled} />
      </Field>
      <Field label="Maksimumspris oppdrag (kr)" error={errs.maxOrderPriceKr}>
        <NumInput value={f.maxOrderPriceKr} onChange={set('maxOrderPriceKr')} min={1} disabled={disabled} />
      </Field>
      <Field label="Faktureringsintervall">
        <select
          value={f.invoiceIntervalDays}
          onChange={e => set('invoiceIntervalDays')(Number(e.target.value))}
          disabled={disabled}
          style={{ width: '100%', padding: '9px 10px', borderRadius: 7, border: `1.5px solid ${COLORS.border}`, fontSize: 14, background: COLORS.white, color: COLORS.textDark }}
        >
          <option value={7}>Ukentlig (7 dager)</option>
          <option value={14}>Annenhver uke (14 dager)</option>
          <option value={30}>Månedlig (30 dager)</option>
        </select>
      </Field>
    </SectionCard>
  );
}
function validateAccess(f) {
  const errs = {};
  if (!Number.isInteger(f.quarantineDaysAfterRejection) || f.quarantineDaysAfterRejection <= 0)
    errs.quarantineDaysAfterRejection = 'Må være et heltall større enn 0';
  if (!Number.isInteger(f.maxActiveBidsPerDriver) || f.maxActiveBidsPerDriver <= 0)
    errs.maxActiveBidsPerDriver = 'Må være et heltall større enn 0';
  return errs;
}

function AccessSection({ settings, savePlatform, disabled }) {
  const [f, setF] = useState({
    requireDriverApproval:        settings.requireDriverApproval,
    quarantineDaysAfterRejection: settings.quarantineDaysAfterRejection,
    maxActiveBidsPerDriver:       settings.maxActiveBidsPerDriver,
  });
  const [errs, setErrs]   = useState({});
  const [status, runSave] = useSaveState();

  useEffect(() => { setF({
    requireDriverApproval:        settings.requireDriverApproval,
    quarantineDaysAfterRejection: settings.quarantineDaysAfterRejection,
    maxActiveBidsPerDriver:       settings.maxActiveBidsPerDriver,
  }); }, [settings]);

  function set(key) { return val => setF(prev => ({ ...prev, [key]: val })); }

  function handleSave() {
    const e = validateAccess(f);
    setErrs(e);
    if (Object.keys(e).length > 0) return;
    runSave(() => savePlatform(f));
  }

  return (
    <SectionCard icon="👤" title="Bruker & tilgang" onSave={handleSave} saveStatus={status}>
      <Field label="Manuell godkjenning av sjåfører">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Toggle checked={f.requireDriverApproval} onChange={set('requireDriverApproval')} disabled={disabled} />
          <span style={{ fontSize: 13, color: COLORS.textMid }}>
            {f.requireDriverApproval ? 'På — nye sjåfører må godkjennes manuelt' : 'Av — sjåfører aktiveres automatisk'}
          </span>
        </div>
      </Field>
      <Field label="Karantenetid etter avvist oppdrag (dager)" error={errs.quarantineDaysAfterRejection}>
        <NumInput value={f.quarantineDaysAfterRejection} onChange={set('quarantineDaysAfterRejection')} min={1} disabled={disabled} />
      </Field>
      <Field label="Maks aktive bud per sjåfør" error={errs.maxActiveBidsPerDriver}>
        <NumInput value={f.maxActiveBidsPerDriver} onChange={set('maxActiveBidsPerDriver')} min={1} disabled={disabled} />
      </Field>
    </SectionCard>
  );
}
function validateOrders(f) {
  const errs = {};
  // Existing fields — must be integers > 0
  ['maxRadiusKm', 'orderExpiryHours', 'maxBidRoundHours'].forEach(k => {
    if (!Number.isInteger(f[k]) || f[k] <= 0)
      errs[k] = 'Må være et heltall større enn 0';
  });
  // New fields — decimals allowed, must be ≥ 0
  if (typeof f.deviationThresholdPct !== 'number' || f.deviationThresholdPct < 0)
    errs.deviationThresholdPct = 'Må være et tall ≥ 0';
  if (typeof f.deviationMinWeightTons !== 'number' || f.deviationMinWeightTons < 0)
    errs.deviationMinWeightTons = 'Må være et tall ≥ 0';
  return errs;
}

function OrdersSection({ settings, savePlatform, disabled }) {
  const [f, setF] = useState({
    maxRadiusKm:            settings.maxRadiusKm,
    orderExpiryHours:       settings.orderExpiryHours,
    maxBidRoundHours:       settings.maxBidRoundHours,
    deviationThresholdPct:  settings.deviationThresholdPct,
    deviationMinWeightTons: settings.deviationMinWeightTons,
  });
  const [errs, setErrs]   = useState({});
  const [status, runSave] = useSaveState();

  useEffect(() => { setF({
    maxRadiusKm:            settings.maxRadiusKm,
    orderExpiryHours:       settings.orderExpiryHours,
    maxBidRoundHours:       settings.maxBidRoundHours,
    deviationThresholdPct:  settings.deviationThresholdPct,
    deviationMinWeightTons: settings.deviationMinWeightTons,
  }); }, [settings]);

  function set(key) { return val => setF(prev => ({ ...prev, [key]: val })); }

  function handleSave() {
    const e = validateOrders(f);
    setErrs(e);
    if (Object.keys(e).length > 0) return;
    runSave(() => savePlatform(f));
  }

  return (
    <SectionCard icon="📦" title="Oppdrag & matching" onSave={handleSave} saveStatus={status}>
      <Field label="Maks radius for å vise oppdrag til sjåfører (km)" error={errs.maxRadiusKm}>
        <NumInput value={f.maxRadiusKm} onChange={set('maxRadiusKm')} min={1} disabled={disabled} />
      </Field>
      <Field label="Automatisk utløp på åpne oppdrag (timer)" error={errs.orderExpiryHours}>
        <NumInput value={f.orderExpiryHours} onChange={set('orderExpiryHours')} min={1} disabled={disabled} />
      </Field>
      <Field label="Maks varighet for budrunde (timer)" error={errs.maxBidRoundHours}>
        <NumInput value={f.maxBidRoundHours} onChange={set('maxBidRoundHours')} min={1} disabled={disabled} />
      </Field>
      <Field label="Avviksterskel for vekt (%)" error={errs.deviationThresholdPct}>
        <NumInput value={f.deviationThresholdPct} onChange={set('deviationThresholdPct')} min={0} disabled={disabled} />
      </Field>
      <Field label="Minimum vekt for avvikskontroll (tonn)" error={errs.deviationMinWeightTons}>
        <NumInput value={f.deviationMinWeightTons} onChange={set('deviationMinWeightTons')} min={0} disabled={disabled} />
      </Field>
    </SectionCard>
  );
}
function PaymentMethodsSection({ settings, savePlatform, disabled }) {
  const invoiceOn = settings.invoicePaymentEnabled === true;
  const [cardOn, setCardOn] = useState(settings.cardPaymentEnabled !== false);
  const [status, runSave]   = useSaveState();

  useEffect(() => { setCardOn(settings.cardPaymentEnabled !== false); }, [settings]);

  // Uten fakturabetaling er kort eneste betalingsmåte, så bryteren er låst på.
  const locked = disabled || !invoiceOn;
  const shown  = invoiceOn ? cardOn : true;

  return (
    <SectionCard
      icon="💳" title="Betalingsmåter" saveStatus={status}
      onSave={() => runSave(() => savePlatform({ cardPaymentEnabled: shown }))}
    >
      <Field label="Kortbetaling (Stripe)">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Toggle checked={shown} onChange={setCardOn} disabled={locked} ariaLabel="Kortbetaling (Stripe)" />
          <span style={{ fontSize: 13, color: COLORS.textMid }}>
            {shown
              ? 'På — bestillere kan betale med kort, og sjåfører kan koble til Stripe'
              : 'Av — kun faktura. Pågående kortordrer fullføres som før'}
          </span>
        </div>
        {!invoiceOn && (
          <p style={{ margin: '6px 0 0', fontSize: 12, color: COLORS.textMid }}>
            Slå på fakturabetaling først. Ellers står plattformen uten betalingsmåte.
          </p>
        )}
      </Field>
    </SectionCard>
  );
}

function ToggleRow({ label, checked, onChange, disabled }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ fontSize: 14, color: COLORS.textDark }}>{label}</span>
      <Toggle checked={checked} onChange={onChange} disabled={disabled} />
    </div>
  );
}

function validateNotify(f) {
  const errs = {};
  if (!Number.isInteger(f.largeOrderThresholdKr) || f.largeOrderThresholdKr <= 0)
    errs.largeOrderThresholdKr = 'Må være et heltall større enn 0';
  return errs;
}

function NotifySection({ settings, savePlatform, disabled }) {
  const [f, setF] = useState({
    notifyOnDispute:       settings.notifyOnDispute,
    notifyOnNewUser:       settings.notifyOnNewUser,
    notifyOnLargeOrder:    settings.notifyOnLargeOrder,
    largeOrderThresholdKr: settings.largeOrderThresholdKr,
  });
  const [errs, setErrs]   = useState({});
  const [status, runSave] = useSaveState();

  useEffect(() => { setF({
    notifyOnDispute:       settings.notifyOnDispute,
    notifyOnNewUser:       settings.notifyOnNewUser,
    notifyOnLargeOrder:    settings.notifyOnLargeOrder,
    largeOrderThresholdKr: settings.largeOrderThresholdKr,
  }); }, [settings]);

  function set(key) { return val => setF(prev => ({ ...prev, [key]: val })); }

  function handleSave() {
    const e = validateNotify(f);
    setErrs(e);
    if (Object.keys(e).length > 0) return;
    runSave(() => savePlatform(f));
  }

  return (
    <SectionCard icon="🔔" title="Varsler" onSave={handleSave} saveStatus={status}>
      <ToggleRow label="Push-varsel ved ny tvist"      checked={f.notifyOnDispute}    onChange={set('notifyOnDispute')}    disabled={disabled} />
      <ToggleRow label="Push-varsel ved ny bruker"      checked={f.notifyOnNewUser}    onChange={set('notifyOnNewUser')}    disabled={disabled} />
      <ToggleRow label="Push-varsel ved stort oppdrag" checked={f.notifyOnLargeOrder} onChange={set('notifyOnLargeOrder')} disabled={disabled} />
      {f.notifyOnLargeOrder && (
        <Field label="Terskelverdi for stort oppdrag (kr)" error={errs.largeOrderThresholdKr}>
          <NumInput value={f.largeOrderThresholdKr} onChange={set('largeOrderThresholdKr')} min={1} disabled={disabled} />
        </Field>
      )}
    </SectionCard>
  );
}
function MaintenanceSection({ settings, saveMaintenance, disabled }) {
  const [f, setF] = useState({
    maintenanceMode:       settings.maintenanceMode,
    maintenanceMessage:    settings.maintenanceMessage,
    flagAllowBidRounds:    settings.flagAllowBidRounds,
    flagAllowFixedPrice:   settings.flagAllowFixedPrice,
    flagRequireDriverDocs: settings.flagRequireDriverDocs,
  });
  const [status, runSave] = useSaveState();

  useEffect(() => { setF({
    maintenanceMode:       settings.maintenanceMode,
    maintenanceMessage:    settings.maintenanceMessage,
    flagAllowBidRounds:    settings.flagAllowBidRounds,
    flagAllowFixedPrice:   settings.flagAllowFixedPrice,
    flagRequireDriverDocs: settings.flagRequireDriverDocs,
  }); }, [settings]);

  function set(key) { return val => setF(prev => ({ ...prev, [key]: val })); }

  return (
    <SectionCard icon="🔧" title="Vedlikehold" onSave={() => runSave(() => saveMaintenance(f))} saveStatus={status}>
      <Field label="Vedlikeholdsmodus">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Toggle checked={f.maintenanceMode} onChange={set('maintenanceMode')} disabled={disabled} />
          <span style={{ fontSize: 13, color: COLORS.textMid }}>
            {f.maintenanceMode ? 'På — brukere ser ikke appen' : 'Av'}
          </span>
        </div>
      </Field>

      {f.maintenanceMode && (
        <div style={{
          background: COLORS.dangerBg, border: `1px solid ${COLORS.danger}`,
          borderRadius: 8, padding: '10px 14px', fontSize: 13, color: COLORS.dangerText,
        }}>
          ⚠️ Vedlikeholdsmodus er aktiv — brukere ser ikke appen
        </div>
      )}

      <Field label="Melding til brukere">
        <textarea
          value={f.maintenanceMessage}
          onChange={e => set('maintenanceMessage')(e.target.value)}
          disabled={disabled}
          placeholder="La stå tom for standardmelding"
          rows={3}
          style={{
            width: '100%', padding: '9px 10px', borderRadius: 7,
            border: `1.5px solid ${COLORS.border}`, fontSize: 14, resize: 'vertical',
            background: COLORS.white, color: COLORS.textDark,
            boxSizing: 'border-box',
          }}
        />
      </Field>

      <div>
        <p style={{ margin: '0 0 12px', fontSize: 13, fontWeight: 600, color: COLORS.textMid, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Feature flags
        </p>
        {[
          { field: 'flagAllowBidRounds',    label: 'Tillat anbudsrunder' },
          { field: 'flagAllowFixedPrice',   label: 'Tillat fastpris-oppdrag' },
          { field: 'flagRequireDriverDocs', label: 'Krev dokumentasjon fra sjåfører' },
        ].map(({ field, label }) => (
          <div key={field} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: 14, color: COLORS.textDark }}>{label}</span>
            <Toggle checked={f[field]} onChange={set(field)} disabled={disabled} />
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

const TEST_STATUS_MESSAGES = {
  ok:              { text: '✓ Sendt',                                         color: COLORS.success },
  'no-token':      { text: 'Brukeren har ikke registrert push-token',          color: COLORS.danger  },
  'token-expired': { text: 'Token utgått — brukeren må åpne appen på nytt',   color: COLORS.danger  },
  err:             { text: 'Kunne ikke sende',                                 color: COLORS.danger  },
};

function TestNotifySection() {
  const [users, setUsers]           = useState([]);
  const [selectedUid, setSelectedUid] = useState('');
  const [status, setStatus]         = useState(null); // null | 'ok' | 'no-token' | 'token-expired' | 'err'
  const [sending, setSending]       = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const timerRef                    = useRef(null);

  useEffect(() => {
    let mounted = true;
    const q = query(collection(db, 'users'), where('fcmToken', '!=', null), limit(100));
    getDocs(q)
      .then(snap => {
        if (!mounted) return;
        const list = snap.docs.map(d => ({ uid: d.id, ...d.data() }));
        setUsers(list);
        if (list.length > 0) setSelectedUid(list[0].uid);
      })
      .catch(() => { if (mounted) setFetchError(true); });
    return () => {
      mounted = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  async function handleSend() {
    if (!selectedUid || sending) return;
    setSending(true);
    setStatus(null);
    try {
      const result = await adminApi.sendTestNotification(selectedUid);
      setStatus(result.ok ? 'ok' : (result.reason ?? 'err'));
    } catch {
      setStatus('err');
    } finally {
      setSending(false);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setStatus(null), 3000);
    }
  }

  return (
    <div style={{
      background: COLORS.white, borderRadius: 12, boxShadow: SHADOWS.sm,
      padding: 24, marginBottom: 20,
    }}>
      <h2 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: COLORS.textDark, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>🧪</span>Test push-varsel
      </h2>
      <hr style={{ border: 'none', borderTop: `1px solid ${COLORS.border}`, marginBottom: 20 }} />

      {fetchError ? (
        <p style={{ fontSize: 14, color: COLORS.danger, margin: 0 }}>
          Kunne ikke laste brukere.
        </p>
      ) : users.length === 0 ? (
        <p style={{ fontSize: 14, color: COLORS.textLight, margin: 0 }}>
          Ingen brukere har registrert push-token ennå.
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: COLORS.textDark, marginBottom: 6 }}>
              Mottaker
            </label>
            <select
              value={selectedUid}
              onChange={e => setSelectedUid(e.target.value)}
              style={{ width: '100%', padding: '9px 10px', borderRadius: 7, border: `1.5px solid ${COLORS.border}`, fontSize: 14, background: COLORS.white, color: COLORS.textDark }}
            >
              {users.map(u => (
                <option key={u.uid} value={u.uid}>
                  {u.displayName ?? u.email ?? u.uid}
                </option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
            {status && TEST_STATUS_MESSAGES[status] && (
              <span style={{ fontSize: 13, color: TEST_STATUS_MESSAGES[status].color }}>
                {TEST_STATUS_MESSAGES[status].text}
              </span>
            )}
            <button
              onClick={handleSend}
              disabled={sending}
              style={{
                padding: '9px 20px', borderRadius: 8, border: 'none',
                background: sending ? COLORS.textLight : COLORS.primary,
                color: '#fff', fontWeight: 600, fontSize: 14,
                cursor: sending ? 'default' : 'pointer',
              }}
            >
              {sending ? 'Sender…' : 'Send test'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────

export default function AdminSettings() {
  const { settings, loading, error, savePricing, savePlatform, saveMaintenance } = useAdminSettings();
  const isMobile = useIsMobile();

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: COLORS.textLight }}>Laster innstillinger…</div>;

  return (
    <div style={{ padding: isMobile ? 16 : '28px 32px', overflowY: 'auto', flex: 1 }}>
      <h1 style={{ margin: '0 0 24px', fontSize: 22, fontWeight: 700, color: COLORS.textDark }}>Innstillinger</h1>
      {error && (
        <div style={{ background: COLORS.dangerBg, border: `1px solid ${COLORS.danger}`, borderRadius: 8, padding: '12px 16px', marginBottom: 20, color: COLORS.dangerText, fontSize: 14 }}>
          Kunne ikke laste innstillinger. Prøv å laste siden på nytt.
        </div>
      )}
      <PricingSection    settings={settings} savePricing={savePricing}   disabled={!!error} />
      <AccessSection     settings={settings} savePlatform={savePlatform} disabled={!!error} />
      <OrdersSection     settings={settings} savePlatform={savePlatform} disabled={!!error} />
      <PaymentMethodsSection settings={settings} savePlatform={savePlatform} disabled={!!error} />
      <NotifySection     settings={settings} savePlatform={savePlatform} disabled={!!error} />
      <MaintenanceSection settings={settings} saveMaintenance={saveMaintenance} disabled={!!error} />
      <TestNotifySection />
    </div>
  );
}
